import 'angular';
import * as approvalTypes from "../approvals-types"
import {unionBy, isEqual, differenceBy, differenceWith} from 'lodash-es';
import SignalProvider from '../../gorillaboy/SignalProvider';
import * as moment from 'moment';
import {IApprovalUtils} from '../ApprovalUtils';

// This structure contains the core info of an approval request (or the store state)
// It only used internally in the store and contains all the aspects of an approval request
interface ApprovalDetails {
    approval: approvalTypes.ApprovalBasicInfo,
    approvalResources: approvalTypes.ApprovalResource[],
    approvalLevelConfigs: approvalTypes.ApprovalLevelConfig[]
};


angular.module('GorillaBoy.Approvals')
.factory('ApprovalDetailsService', [
    '$rootScope',
    '$log',
    '$translate',
    'ApprovalUtils',
    function ApprovalDetailsService($rootScope: ng.IRootScopeService, $log: ng.ILogService, $translate: ng.translate.ITranslateService, ApprovalUtils: IApprovalUtils) {
        // store properties
        let approval: approvalTypes.ApprovalBasicInfo = initApproval();
        let approvalResources: approvalTypes.ApprovalResource[] = initApprovalResources();
        let approvalLevelConfigs: approvalTypes.ApprovalLevelConfig[] = initApprovalLevelConfig();
        let approvalApprovers: approvalTypes.ApprovalApprover[][] = initApprovalApprovers();
        let open: boolean = false;
        let mode: approvalTypes.ApprovalDetailsServiceMode = null;
        let previousApprovalDetails: ApprovalDetails = null;

        // methods
        let approvalDetailsService = {} as approvalTypes.ApprovalDetailsService;

        approvalDetailsService.initService = (approvalRequest?: approvalTypes.ApprovalInfo, storeMode?: approvalTypes.ApprovalDetailsServiceMode) => {
            // Init approval, approvalLevelConfigs, approvalResources, and open
            if (approvalRequest) {
                approval = {
                    Id: approvalRequest.Id,
                    Title: approvalRequest.Title,
                    Description: approvalRequest.Description,
                    Owner: approvalRequest.Owner,
                    CreationDate: approvalRequest.CreationDate,
                    ModificationDate: approvalRequest.ModificationDate,
                    PublishDate: approvalRequest.PublishDate,
                    CompleteDate: approvalRequest.CompleteDate,
                    TemplateId: approvalRequest.TemplateId,
                    State: approvalRequest.State,
                    CurrentLevel: approvalRequest.CurrentLevel,
                    CurrentLevelDueDate: approvalRequest.CurrentLevelDueDate,
                    Properties: approvalRequest.Properties
                };
                approvalLevelConfigs = approvalRequest.LevelConfigs;
                approvalResources = approvalRequest.Resources;
                open = false;
            } else {
                approval = initApproval();
                approvalLevelConfigs = initApprovalLevelConfig();
                approvalResources = initApprovalResources();
                open = false;
            }
            
            // Init mode
            if (storeMode) {
                mode = storeMode;
            } else {
                // default to create mode
                mode = approvalTypes.ApprovalDetailsServiceMode.CREATE;
            }

            // Copy the initial value of the store if mode is edit to get the diff in the future
            if (mode === approvalTypes.ApprovalDetailsServiceMode.EDIT) {
                setPreviousApprovalDetails(approval, approvalResources, approvalLevelConfigs);
            } else {
                // clear the previous core store state if it is not in edit mode
                previousApprovalDetails = null;
            }
        };

        approvalDetailsService.getPreviousRequestId = () => {
            return previousApprovalDetails.approval.Id;
        }

        approvalDetailsService.getServiceMode = () => {
            return mode;
        }

        approvalDetailsService.isPanelVisiable = () => {
            return open;
        };

        approvalDetailsService.setPanelVisibility = (visibility: boolean) => {
            open = visibility;
            return open;
        };

        approvalDetailsService.getApproval = () => {
            return JSON.parse(JSON.stringify(approval));
        };

        approvalDetailsService.setApproval = (newApproval: any) => {
            // copy the value instead of replace the reference
            approval = JSON.parse(JSON.stringify(newApproval));
            return approval;
        };

        approvalDetailsService.getApprovalResources = () => {
            return JSON.parse(JSON.stringify(approvalResources));
        };

        approvalDetailsService.appendApprovalResource = (approvalResource: approvalTypes.ApprovalResource) => {
            // use unionBy to prevent duplicate entry
            approvalResources = unionBy(approvalResources, [approvalResource], 'ResourceUri');
            $rootScope.$broadcast(SignalProvider.Approval.newApproval.approvalResourceUpdated);
            return approvalResources;
        };

        approvalDetailsService.deleteApprovalResourceByUri = (uri: string) => {
            approvalResources = approvalResources.filter((approvalResource: approvalTypes.ApprovalResource) => {
                return approvalResource.ResourceUri !== uri;
            });
            $rootScope.$broadcast(SignalProvider.Approval.newApproval.approvalResourceUpdated);
            return approvalResources;
        };

        approvalDetailsService.getApprovalApprovers = () => {
            return getApproversFromLevelConfigs(approvalLevelConfigs);
        };

        approvalDetailsService.getApprovalApproversByLevelNumber = (levelNum: number) => {
            return JSON.parse(JSON.stringify(approvalApprovers[levelNum]));
        };

        approvalDetailsService.appendApprovalApproverLevel = (approvers: approvalTypes.ApprovalApprover[]) => {
            approvalApprovers.push(approvers);
            return approvalApprovers;
        };

        approvalDetailsService.appendApprovalApproverToLevel = (levelNum: number, approver: approvalTypes.ApprovalApprover) => {
            // use unionBy to prevent duplicate entry
            approvalApprovers[levelNum] = unionBy(approvalApprovers[levelNum], [approver], 'UserId');
            return approvalApprovers;
        };

        approvalDetailsService.deleteApprovalApproverLevelByLevelNumber = (levelNum: number) => {
            approvalApprovers.splice(levelNum, 1);
            return approvalApprovers;
        };

        approvalDetailsService.deleteApprovalApproverByUserId = (id: string) => {
            for (var approverLevel of approvalApprovers) {
                approverLevel = approverLevel.filter((approver: approvalTypes.ApprovalApprover) => {
                    return approver.UserId !== id;
                });
            }
            return approvalApprovers;
        };

        approvalDetailsService.getApprovalLevelConfigs = () => {
            return JSON.parse(JSON.stringify(approvalLevelConfigs));
        };

        approvalDetailsService.setApprovalLevelConfigs = (levelConfigs: approvalTypes.ApprovalLevelConfig[]) => {
            approvalLevelConfigs = JSON.parse(JSON.stringify(levelConfigs));
            return approvalLevelConfigs;
        }

        approvalDetailsService.appendApprovalLevelConfig = (levelConfig: approvalTypes.ApprovalLevelConfig) => {
            approvalLevelConfigs.push(levelConfig);
            return approvalLevelConfigs;
        };

        approvalDetailsService.deleteApprovalLevelConfigByLevelNumber = (levelNum: number) => {
            let deletedLevelNum = null;
            approvalLevelConfigs = approvalLevelConfigs.filter((levelConfig: approvalTypes.ApprovalLevelConfig, index: number) => {
                if (levelConfig.LevelNumber === levelNum) {
                    deletedLevelNum = index;
                }
                return levelConfig.LevelNumber !== levelNum;
            });

            // Reindex approvalLevelConfigs and approvers
            if (deletedLevelNum !== null) {
                approvalLevelConfigs = approvalLevelConfigs.map(function(levelConfig: approvalTypes.ApprovalLevelConfig, index: number) {
                    levelConfig.LevelNumber = index;
                    return levelConfig;
                });

                approvalDetailsService.deleteApprovalApproverLevelByLevelNumber(deletedLevelNum);
            }
            
            return approvalLevelConfigs;
        };

        approvalDetailsService.isApprovalRequestChanged = () => {
            let previousApprovalRequestDTO = ApprovalUtils.composeApprovalRequestDTO(previousApprovalDetails.approval, previousApprovalDetails.approvalLevelConfigs);
            let currentApprovalRequestDTO = ApprovalUtils.composeApprovalRequestDTO(approval, approvalLevelConfigs);

            if (!isEqual(currentApprovalRequestDTO, previousApprovalRequestDTO)) {
                return true;
            } else {
                return false;
            }
        };

        approvalDetailsService.getApprovalRequestDTO = () => {
            let currentApprovalRequestDTO = ApprovalUtils.composeApprovalRequestDTO(approval, approvalLevelConfigs);
            return currentApprovalRequestDTO;
        };

        approvalDetailsService.getAddedApprovers = () => {
            let previousApprovers = getApproversFromLevelConfigs(previousApprovalDetails.approvalLevelConfigs);
            let currentApprovers = getApproversFromLevelConfigs(approvalLevelConfigs);

            let addedApprovers = differenceWith(currentApprovers, previousApprovers, approverComparator);
            return addedApprovers;
        };

        approvalDetailsService.getRemovedApprovers = () => {
            let previousApprovers = getApproversFromLevelConfigs(previousApprovalDetails.approvalLevelConfigs);
            let currentApprovers = getApproversFromLevelConfigs(approvalLevelConfigs);

            let removedApprovers = differenceWith(previousApprovers, currentApprovers, approverComparator);
            return removedApprovers;
        };

        approvalDetailsService.getAddedResources = () => {
            let previousResources = previousApprovalDetails.approvalResources
            let currentResources = approvalResources;
            let addedResources = differenceBy(currentResources, previousResources, 'ResourceUri');
            return addedResources;
        }

        approvalDetailsService.getRemovedResources = () => {
            let previousResources = previousApprovalDetails.approvalResources
            let currentResources = approvalResources;
            let removedResources = differenceBy(previousResources, currentResources, 'ResourceUri');
            return removedResources;
        }

        approvalDetailsService.isResourceModificationAllowed = () => {
            if (mode === approvalTypes.ApprovalDetailsServiceMode.EDIT) {
                if (approval.State === approvalTypes.ApprovalState.DRAFT || approval.State === approvalTypes.ApprovalState.PUBLISHED) {
                    // Above are the only two non-actioned, non-decided states
                    return true;
                } else {
                    return false;
                }
            }

            // default to true
            return true;
        }
        
        // Helper functions
        function initApproval(): approvalTypes.ApprovalBasicInfo {
            return {
                Id: null,
                Title: $translate.instant('APPROVALS.GENERAL.NEW_APPROVAL_DEFAULT_TITLE') + ' ' + moment(new Date()).format("MM/DD/YYYY HH:mm A"),
                Description: '',
                Owner: null,
                CreationDate: null,
                ModificationDate: null,
                PublishDate: null,
                CompleteDate: null,
                TemplateId: null,
                State: approvalTypes.ApprovalState.DRAFT,
                CurrentLevel: null,
                CurrentLevelDueDate: null,
                Properties: []
            };
        }

        function initApprovalResources() {
            return [];
        }

        function initApprovalLevelConfig() {
            return [];
        }

        function initApprovalApprovers() {
            return [];
        }

        function setPreviousApprovalDetails(approval: approvalTypes.ApprovalBasicInfo, resources: approvalTypes.ApprovalResource[], levelConfigs: approvalTypes.ApprovalLevelConfig[]): ApprovalDetails {
            // It is important to copy the value instead of references
            let coreState: ApprovalDetails = {
                approval: JSON.parse(JSON.stringify(approval)),
                approvalResources: JSON.parse(JSON.stringify(resources)),
                approvalLevelConfigs: JSON.parse(JSON.stringify(levelConfigs))
            }
            previousApprovalDetails = coreState;
            return coreState;
        }

        function getApproversFromLevelConfigs(levelConfigs: approvalTypes.ApprovalLevelConfig[]): approvalTypes.ApprovalApprover[] {
            let result: approvalTypes.ApprovalApprover[] = [];
            levelConfigs.forEach(level => {
                result = result.concat(level.approvers);
            });
            return result;
        }

        function approverComparator(baseApprover: approvalTypes.ApprovalApprover, otherApprover: approvalTypes.ApprovalApprover): boolean {
            return baseApprover.UserId === otherApprover.UserId
                && baseApprover.ApproverKind === otherApprover.ApproverKind
                && baseApprover.Level === otherApprover.Level;
        }

        return approvalDetailsService;
    }
]);